/**
 * Single URL builder for apps/web.
 *
 * Pages and services must not concatenate env.API_URL or strip /api themselves.
 * Use `api` / `reporting` from `@/app/config.js`.
 */

const REST_PREFIX = '/api';

function trimSlash(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function asPath(path) {
  const raw = String(path ?? '').trim();
  if (!raw || raw === '/') return '';
  return raw.startsWith('/') ? raw : `/${raw}`;
}

function joinUrl(base, path) {
  const root = trimSlash(base);
  const suffix = asPath(path);
  return suffix ? `${root}${suffix}` : root;
}

function isAbsoluteHref(url) {
  return /^(?:https?:|blob:|data:)/i.test(String(url || ''));
}

export function resolveApiOrigin(rawOrigin) {
  const raw = trimSlash(rawOrigin);
  if (!raw || raw === REST_PREFIX || raw === '/v1') return '';
  try {
    const url = new URL(raw);
    if (
      url.pathname === REST_PREFIX
      || url.pathname.startsWith(`${REST_PREFIX}/`)
      || url.pathname === '/v1'
      || url.pathname.startsWith('/v1/')
    ) {
      return url.origin;
    }
    return trimSlash(raw.replace(/\/(api|v1)$/i, ''));
  } catch {
    return trimSlash(raw.replace(/\/(api|v1)$/i, ''));
  }
}

/**
 * @param {{ origin?: string }} [options]
 */
export function createApiUrls({ origin } = {}) {
  const resolvedOrigin = resolveApiOrigin(origin) || trimSlash(origin);
  const restBase = resolvedOrigin ? `${resolvedOrigin}${REST_PREFIX}` : REST_PREFIX;

  return {
    /** API process origin. SDK baseURL, /uploads, /health, Socket.IO. */
    origin: resolvedOrigin,
    /** REST base with prefix: `{origin}/api` */
    restBase,
    /** Absolute REST URL: `api.url('/document/1')` */
    url(path) {
      return joinUrl(restBase, path);
    },
    /** Same-origin REST path: `api.path('/admin/queues')` → `/api/admin/queues` */
    path(path) {
      return joinUrl(REST_PREFIX, path);
    },
    /**
     * File or stored path on the API process (`/uploads/...`).
     * Absolute http(s) / blob / data URLs pass through.
     */
    resource(path) {
      if (path == null || path === '') return path;
      const raw = String(path);
      if (isAbsoluteHref(raw)) return raw;
      return joinUrl(resolvedOrigin, raw);
    },
    /** Strip origin for persisted `/uploads` values. blob/data → null. */
    toRelativeResource(url) {
      if (url == null || url === '') return url;
      const raw = String(url);
      if (raw.startsWith('blob:') || raw.startsWith('data:')) return null;
      if (resolvedOrigin && raw.startsWith(resolvedOrigin)) {
        return raw.slice(resolvedOrigin.length) || '/';
      }
      return raw;
    },
  };
}

/**
 * @param {{ origin?: string }} [options]
 */
export function createReportingUrls({ origin } = {}) {
  const resolvedOrigin = trimSlash(origin);
  return {
    origin: resolvedOrigin,
    url(path) {
      return joinUrl(resolvedOrigin, path);
    },
  };
}
